// HR documents found in the chosen SharePoint sources, and the review gate in front of answers.
//
//   sync     finds files, extracts text, and records it as the document's `current` version
//   review   an HR admin approves that exact version (or excludes the document)
//   publish  compiles every approved, still-present document into an immutable corpus
//            snapshot; answers only ever read a published snapshot
//
// A sync never changes what employees see. Neither does approving: only publishing does.

import { AUDIENCES, appliesTo, type ApplicabilityValue, type Audience, type SharePointSource } from "./sources.js";

export interface Page {
  n: number;
  label: string; // "Page 3" for PDFs, the heading for Word sections
  text: string;
}

export interface DocVersion {
  hash: string; // sha256 of the extracted pages
  pages: number;
  chars: number;
  tokens: number;
  modified: string; // SharePoint lastModifiedDateTime of the file this came from
  extractedAt: string;
  // "images": a scanned PDF, its text read from the page images by Claude. HR checks it against
  // the PDF in review.
  readFrom?: "images";
}

export type DocState = "active" | "removed" | "error";

export interface HrDocument {
  id: string; // `${driveId}~${itemId}`
  driveId: string;
  driveName: string;
  itemId: string;
  name: string;
  path: string; // folder path inside the library, "/" at the top
  webUrl: string;
  sourceId: string;
  cTag: string;
  size: number;
  modified: string;
  state: DocState;
  error?: string;
  current?: DocVersion;
  approved?: DocVersion;
  approvedBy?: string;
  approvedAt?: string;
  excludedBy?: string;
  excludedAt?: string;
  // Set by an HR admin when this document's audience differs from its source's (the CA guide in
  // a folder of all-employee policies). Absent means "same as the source".
  applicability?: ApplicabilityValue;
  applicabilityBy?: string;
  applicabilityAt?: string;
  // Location variants: documents HR grouped under one name ("Employee Guide") because they are
  // versions of the same thing for different places. See forAudience.
  variantGroup?: string;
  variantGroupBy?: string;
  variantGroupAt?: string;
  firstSeen: string;
  lastSeen: string;
  removedAt?: string;
}

export type ReviewStatus = "new" | "changed" | "approved" | "excluded" | "removed" | "error";

export const REVIEW_LABELS: Record<ReviewStatus, string> = {
  new: "New: needs review",
  changed: "Changed: needs review",
  approved: "Approved",
  excluded: "Excluded",
  removed: "Removed from SharePoint",
  error: "Couldn't read",
};

export function reviewStatus(d: HrDocument): ReviewStatus {
  if (d.state === "removed") return "removed";
  if (d.excludedAt) return "excluded";
  if (d.state === "error") return "error";
  if (!d.approved) return "new";
  if (d.current && d.current.hash !== d.approved.hash) return "changed";
  return "approved";
}

export function needsReview(d: HrDocument): boolean {
  const s = reviewStatus(d);
  return s === "new" || s === "changed";
}

// Documents in a folder named "Archive", at any depth and in any capitalisation, are old copies HR
// keeps for the record. Admin lists them apart, under Archive, so they don't crowd the review
// list; HR can still approve one from there.
export function isArchived(d: Pick<HrDocument, "path">): boolean {
  return d.path.split("/").some((folder) => folder.trim().toLowerCase() === "archive");
}

// ---- location variants

export const MAX_VARIANT_GROUP_CHARS = 80;

// What one audience reads. Every document that applies to them, except that inside a variant group
// the version written for their location replaces the rest: a California employee gets the
// California guide and not the general one, while employees elsewhere get the general one (set
// to "All employees", it is the fallback). A group with no version for this audience gives them
// whatever in it applies to them.
export function forAudience<T extends { applicability: ApplicabilityValue; variantGroup?: string | undefined }>(
  docs: readonly T[],
  audience: Audience,
): T[] {
  const applicable = docs.filter((d) => appliesTo(d.applicability, audience));
  const specific = new Set(applicable.filter((d) => d.variantGroup && d.applicability === audience).map((d) => d.variantGroup));
  return applicable.filter((d) => !d.variantGroup || !specific.has(d.variantGroup) || d.applicability === audience);
}

// What several audiences read together (an asker whose location isn't known gets every variant,
// and is asked where they work when it matters), in the input's order.
export function forAudiences<T extends { applicability: ApplicabilityValue; variantGroup?: string | undefined }>(
  docs: readonly T[],
  audiences: readonly Audience[],
): T[] {
  const keep = new Set(audiences.flatMap((a) => forAudience(docs, a)));
  return docs.filter((d) => keep.has(d));
}

export const SUPPORTED_EXTENSIONS = [".pdf", ".docx"];

export function isSupportedFile(name: string): boolean {
  const lower = name.toLowerCase();
  return SUPPORTED_EXTENSIONS.some((e) => lower.endsWith(e));
}

export function textKey(docId: string, hash: string): string {
  return `text/${docId}/${hash}.json`;
}

export function corpusKey(version: number): string {
  return `corpus/v${version}.json`;
}

// Tokens a document costs in a prompt, for the budget guard. Measured on Sonnet 5.5 through
// Bedrock on 2026-10-08 (input_tokens of one-document requests, citations on): HR PDF text runs
// about 3.1 characters per token, and citations add about 800 tokens per document plus a little
// per page. This lands within about 10% of the real counts, erring high:
//   CA employee guide   144,808 chars, 50 pages  -> real 46,136, estimate 50,570
//   Travel & Expense     11,259 chars,  6 pages  -> real  4,760, estimate  4,733
//   Holidays 2026         1,096 chars,  1 page   -> real  1,328, estimate  1,196
export function estimateTokens(chars: number, pages = 1): number {
  return Math.ceil(chars / 3) + 30 * pages + 800;
}

// ---- publishing

// Who a document applies to: its own setting if HR set one, otherwise its source's.
export function effectiveApplicability(
  doc: Pick<HrDocument, "applicability">,
  source: Pick<SharePointSource, "applicability"> | undefined,
): ApplicabilityValue | undefined {
  return doc.applicability ?? source?.applicability;
}

export interface CorpusMember {
  doc: HrDocument;
  version: DocVersion; // the approved version
  applicability: ApplicabilityValue; // from the document's source, at publish time
  variantGroup?: string;
}

// What a publish right now would contain: approved, not excluded, not removed, and still inside
// a chosen source. A document whose latest sync failed keeps its approved version.
export function corpusMembers(docs: readonly HrDocument[], sources: readonly SharePointSource[]): CorpusMember[] {
  const byId = new Map(sources.map((s) => [s.id, s]));
  const out: CorpusMember[] = [];
  for (const doc of docs) {
    const source = byId.get(doc.sourceId);
    // Moved into an Archive folder: out of answers, like on the Documents tab.
    if (!source || !doc.approved || doc.excludedAt || doc.state === "removed" || isArchived(doc)) continue;
    out.push({
      doc,
      version: doc.approved,
      applicability: doc.applicability ?? source.applicability,
      ...(doc.variantGroup ? { variantGroup: doc.variantGroup } : {}),
    });
  }
  return out.sort((a, b) => `${a.doc.driveName}${a.doc.path}/${a.doc.name}`.localeCompare(`${b.doc.driveName}${b.doc.path}/${b.doc.name}`));
}

// Tokens each audience's answers would carry: what forAudience gives them.
export function audienceTokens(
  members: readonly { applicability: ApplicabilityValue; variantGroup?: string | undefined; tokens: number }[],
): Record<Audience, number> {
  return Object.fromEntries(AUDIENCES.map((a) => [a, forAudience(members, a).reduce((n, m) => n + m.tokens, 0)])) as Record<
    Audience,
    number
  >;
}

export interface PublishedEntry {
  id: string;
  hash: string;
  applicability: ApplicabilityValue;
  variantGroup?: string;
}

export interface CorpusDiff {
  added: string[];
  updated: string[]; // new approved text, a different audience, or a different variant group
  removed: string[];
}

export function corpusDiff(members: readonly CorpusMember[], published: readonly PublishedEntry[]): CorpusDiff {
  const before = new Map(published.map((p) => [p.id, p]));
  const diff: CorpusDiff = { added: [], updated: [], removed: [] };
  for (const m of members) {
    const p = before.get(m.doc.id);
    if (!p) diff.added.push(m.doc.id);
    else if (p.hash !== m.version.hash || p.applicability !== m.applicability || (p.variantGroup ?? "") !== (m.variantGroup ?? "")) {
      diff.updated.push(m.doc.id);
    }
    before.delete(m.doc.id);
  }
  diff.removed = [...before.keys()];
  return diff;
}

export function hasChanges(d: CorpusDiff): boolean {
  return d.added.length + d.updated.length + d.removed.length > 0;
}

// ---- the published snapshot (S3 corpus/v<n>.json) and its index record

export interface CorpusDocument {
  id: string;
  name: string;
  library: string;
  path: string;
  webUrl: string;
  applicability: ApplicabilityValue;
  variantGroup?: string;
  modified: string;
  hash: string;
  pages: Page[];
}

export interface CorpusSnapshot {
  version: number;
  publishedAt: string;
  publishedBy: string;
  tokens: Record<Audience, number>;
  documents: CorpusDocument[];
}

export interface CorpusVersion {
  version: number;
  publishedAt: string;
  publishedBy: string;
  key: string;
  documentCount: number;
  tokens: Record<Audience, number>;
  entries: PublishedEntry[];
}

// ---- sync runs

export interface SyncSummary {
  trigger: string;
  startedAt: string;
  finishedAt: string;
  sources: number;
  sourceErrors: number;
  files: number;
  new: number;
  changed: number;
  unchanged: number;
  removed: number;
  errors: number;
  unsupported: number;
  scanned?: number; // scanned PDFs read from their page images this sync
}
